/* eslint-disable no-shadow, no-param-reassign */
import { make } from 'vuex-pathify';
import { find } from 'lodash';

//words included in route names to use for identifying in what type of route we are
//DEV NOTE: 'show' currently not supported because we need enchanced logic for child/tab routes
const ROUTE_NAME_TYPE_WORDS = ['index', 'new', 'edit'];

const state = {
  pwaInstallEvent: undefined,
  // REFACTOR: The keys below are exclusively coupled to global control of TheAppCardWrapper styling.
  maxBreakpoint: undefined,
  isFluid: false,

  // ROUTING variables
  currentRoute: undefined,
  previousRoute: undefined,

  //FORM variables
  step: 0,
  lastStep: 0,
  isValidationRequired: false,
  hasValidationErrorsInCurrentStep: false,
  passedValidation: false,
  validationErrors: [],

  isReadyToSubmitForm: false,
  isReadyToCheckValidation: false,
  isFromShow: false,
  //lock form step variables from being able to be changed
  formStepVariablesLocked: false,
  attachmentTokens: undefined,
  activeUploads: [],
};

const getters = {
  pwaInstallable: state => {
    const onMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    return onMobile && state.pwaInstallEvent !== undefined;
  },
  formStepVariablesLocked: state => {
    return state.formStepVariablesLocked;
  },
  //ROUTING VARIABLES
  previousRoute: state => {
    return state.previousRoute;
  },
  previousRouteName: state => {
    return state.previousRoute?.name;
  },
  currentRoute: state => {
    return state.currentRoute;
  },
  currentRouteName: state => {
    return state.currentRoute?.name;
  },
  currentRouteResource: state => {
    return state.currentRoute?.meta?.resource || (state.currentRoute.matched.filter(x => x.meta?.resource).at(-1)?.meta?.resource);
  },
  currentRouteAction: state => {
    return state.currentRoute?.meta?.action;
  },
  currentRouteType: (_state, getters) => {
    let lowerCaseCurrentRouteType = getters.currentRoute?.meta?.action || '';
    if (lowerCaseCurrentRouteType === undefined) return;
    return find(ROUTE_NAME_TYPE_WORDS, word => lowerCaseCurrentRouteType.includes(word.toLowerCase()));
  },
  isCurrentRouteAType: (_state, getters) => (type) => {
    return getters.currentRouteType == type;
  },
  isCurrentRouteAForm: (_state, getters) => {
    return getters.isCurrentRouteAType('new') || getters.isCurrentRouteAType('edit');
  },

  //FORM Step variables
  currentStep: state => {
    return state.step;
  },
  lastStep: state => {
    return state.lastStep;
  },
  isOnFirstStepOfForm: state => {
    return state.step === 0;
  },
  isOnLastStepOfForm: state => {
    return state.step === state.lastStep;
  },
  validationErrors: state => {
    return state.validationErrors;
  },
  hasValidationErrorsInCurrentStep: state => {
    return state.hasValidationErrorsInCurrentStep;
  },
  isValidationRequired: state => {
    return state.isValidationRequired;
  },
  isReadyToSubmitForm: state => {
    return state.isReadyToSubmitForm;
  },
  isReadyToCheckValidation: state => {
    return state.isReadyToCheckValidation;
  },
  isFromShow: state => {
    return state.isFromShow;
  },
  attachmentTokens: state => {
    return state.attachmentTokens;
  },
  passedValidation: state => {
    return state.passedValidation;
  },
  activeUploads: state => {
    return state.activeUploads;
  },
};
const mutations = {
  ...make.mutations(state),
  //ROUTE mutations
  setPreviousRoute(state, route) {
    state.previousRoute = route;
  },
  setCurrentRoute(state, route) {
    state.currentRoute = route;
  },

  //FORM Step mutations
  setFormStepVariablesLocked(state, bool) {
    state.formStepVariablesLocked = bool;
  },
  setStep(state, num) {
    state.step = num;
  },
  setLastStep(state, num) {
    state.lastStep = num;
  },
  setValidationErrors(state, arr) {
    state.validationErrors = arr;
  },
  setIsValidationRequired(state, bool) {
    state.isValidationRequired = bool;
  },
  setHasValidationErrorsInCurrentStep(state, bool) {
    state.hasValidationErrorsInCurrentStep = bool;
  },
  setIsReadyToSubmitForm(state, bool) {
    state.isReadyToSubmitForm = bool;
  },
  setIsReadyToCheckValidation(state, bool) {
    state.isReadyToCheckValidation = bool;
  },
  setPassedValidation(state, bool) {
    state.passedValidation = bool;
  },
  setIsFromShow(state, bool) {
    state.isFromShow = bool;
  },
  setAttachmentTokens(state, tokens) {
    state.attachmentTokens = tokens;
  },
  addActiveUpload(state, val) {
    state.activeUploads.push(val);
  },
  removeActiveUpload(state, val) {
    state.activeUploads = state.activeUploads.filter(upload => upload.key != val?.key);
  },
};

const actions = {
  approvePWA({ commit, state, dispatch }) {
    const { pwaInstallEvent } = state;

    // For testing purposes only
    if (typeof pwaInstallEvent === 'string') {
      dispatch('denyPWA');
      return;
    }
    // end testing

    if (pwaInstallEvent != undefined) {
      pwaInstallEvent.prompt();
      pwaInstallEvent.userChoice.then(result => {
        if (result.outcome === 'accepted') {
          commit('pwaInstallEvent', undefined);
        }
      });
    }
  },
  denyPWA({ commit }) {
    commit('pwaInstallEvent', undefined);
  },
  setMaxBreakpoint({ commit, state }, v) {
    if (state.maxBreakpoint === v) {
      return;
    }
    if (Object.keys(this.$app.$vuetify?.breakpoint.thresholds).includes(v)) {
      commit('maxBreakpoint', v);
    } else {
      commit('maxBreakpoint', undefined);
    }
  },
  //ROUTE actions
  setCurrentRoute({ commit, dispatch }, route) {
    if (route === undefined || route.name === undefined) return;

    commit('setCurrentRoute', route);
    dispatch('resetFormStepVariables');
  },
  setPreviousRoute({ commit }, route) {
    commit('setPreviousRoute', route);
  },

  //FORM Step actions
  goToNextStep({ dispatch, state, getters }) {
    if (!dispatch('goToStep', state.step + 1)) return;

    if (!getters.isValidationRequired) {
      dispatch('setIsReadyToCheckValidation', false);
    }
  },
  goToPreviousStep({ dispatch, state }) {
    if (!dispatch('goToStep', state.step - 1)) return;
  },
  goToStep({ commit, getters, dispatch }, num = 0) {
    if (getters.isValidationRequired && getters.hasValidationErrorsInCurrentStep) return;

    let number = Number(num);
    if (!getters.formStepVariablesLocked) {
      if (number >= 0 && number <= getters.lastStep) {
        dispatch('setIsReadyToSubmitForm', number == getters.lastStep + 1);
        commit('setStep', number);
        if (!getters.isValidationRequired)
          dispatch('setPassedValidation', false);
        return true;
      }
    }
    //if our next step would be more than the total steps of the form, then we assume that we want to submit the form
    dispatch('setIsReadyToSubmitForm', number == getters.lastStep + 1);
  },
  setFormStepVariablesLocked({ commit }, bool) {
    commit('setFormStepVariablesLocked', bool);
  },
  setLastStep({ commit, getters }, num) {
    if (!getters.formStepVariablesLocked)
      commit('setLastStep', num);
  },
  setIsValidationRequired({ commit, getters }, bool) {
    if (!getters.formStepVariablesLocked)
      commit('setIsValidationRequired', bool);
  },
  setHasValidationErrorsInCurrentStep({ commit, getters }, bool) {
    if (!getters.formStepVariablesLocked) {
      commit('setHasValidationErrorsInCurrentStep', bool);
    }
  },
  setIsReadyToSubmitForm({ commit, getters }, bool) {
    if (!getters.formStepVariablesLocked)
      commit('setIsReadyToSubmitForm', bool);
  },
  setIsReadyToCheckValidation({ commit, getters }, bool) {
    if (!getters.formStepVariablesLocked)
      commit('setIsReadyToCheckValidation', bool);
  },
  setPassedValidation({ commit, getters }, bool) {
    if (!getters.formStepVariablesLocked)
      commit('setPassedValidation', bool);
  },
  setAttachmentTokens({ commit }, tokens) {
    commit('setAttachmentTokens', tokens);
  },
  setIsFromShow({ commit }, bool) {
    // Check for both boolean true and string "true", as value will be coming from $route.query which is a string
    commit('setIsFromShow', bool === true || bool === "true");
  },
  addActiveUpload({ commit }, val) {
    commit('addActiveUpload', val);
  },
  removeActiveUpload({ commit }, val) {
    commit('removeActiveUpload', val);
  },
  setValidationErrors({ commit, getters }, arr) {
    if (!getters.formStepVariablesLocked)
      commit('setValidationErrors', arr);
  },
  resetValidationErrors({ commit, getters }) {
    if (!getters.formStepVariablesLocked)
      commit('setValidationErrors', []);
  },
  resetFormStepVariables({ dispatch }) {
    dispatch('goToStep', 0);
    dispatch('setLastStep', 0);
    dispatch('setIsValidationRequired', false);
    dispatch('setIsReadyToSubmitForm', false);
    dispatch('setIsReadyToCheckValidation', false);
    dispatch('setPassedValidation', false);
  },
};

export default {
  namespaced: true,
  state: () => state,
  getters,
  mutations,
  actions,
};
